import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const BillingCard = () => {
  const [number, setNumber] = useState("");
  const [rate, setRate] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/billing");
      setNumber(data.paid_line_number);
      setRate(Number(data.per_minute_rate).toFixed(2));
    } catch {
      toast.error("Could not load billing settings");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const r = parseFloat(rate);
    if (Number.isNaN(r) || r < 0 || r > 10) {
      toast.error("Rate must be between $0.00 and $10.00 per minute");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/billing", { paid_line_number: number, per_minute_rate: r });
      setNumber(data.paid_line_number);
      setRate(Number(data.per_minute_rate).toFixed(2));
      toast.success(`Billing updated — $${Number(data.per_minute_rate).toFixed(2)}/min on ${data.paid_line_number}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save billing settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border bg-[#0A0A0A] rounded-sm p-6 space-y-4">
      <div className="flex items-center gap-2">
        <PhoneCall className="w-4 h-4 text-[#007AFF]" />
        <h2 className="font-heading text-lg font-black tracking-tight">Call billing</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Calls received on the paid line below are charged per minute (rounded up, 1-minute minimum) from
        the caller's credit balance. Calls to users' own numbers stay free. Reports are always saved even
        if the balance can't cover the charge.
      </p>
      <div className="grid sm:grid-cols-3 gap-3 items-end">
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Paid line number</Label>
          <Input value={number} onChange={(e) => setNumber(e.target.value)} data-testid="billing-number-input"
            className="mt-2 bg-background border-border rounded-sm font-mono focus-visible:ring-[#007AFF]" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Rate per minute</Label>
          <div className="relative mt-2">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">$</span>
            <Input type="number" min="0" max="10" step="0.01" value={rate}
              onChange={(e) => setRate(e.target.value)} data-testid="billing-rate-input"
              className="pl-7 bg-background border-border rounded-sm font-mono focus-visible:ring-[#007AFF]" />
          </div>
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={save} disabled={saving} data-testid="billing-save-btn"
        className="rounded-sm border-border text-xs">
        {saving ? "Saving…" : "Save billing settings"}
      </Button>
    </div>
  );
};
